import React, { useEffect } from "react"
import { View } from "react-native"
import { WidgetCommonFTUContainer } from "@/components/widgets/WidgetCommonContainer"
import BottomIcon from "@/assets/clipart/widgets/leads-bottom-icon.svg"
import MyLeadsGaugeIndicator from "@/assets/clipart/widgets/myleads-gauge-indicator.svg"
import DialComponent from "@/components/widgets/DialComponent"
import Spacer from "@/components/util/Spacer"
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from "react-native-reanimated"
import { incrementWidgetViewCount } from "@/stores/myLeads"
import { useUser } from "@/queries/user"
import { WidgetWrapper } from "./WidgetWrapper"
import { router } from "expo-router"

import * as HomeEvents from "@/analytics/events/home"
import BFText, { H3 } from "@/components/standard/BFText"
import { useViewedCallback } from "@/analytics/react"
import { useMyLeadsBoostFeature } from "@/hooks/useMyLeadsBoostFeature"
import PocketImage from "@/assets/clipart/widgets/properties-nudge-image.svg"
import GiftImage from "@/assets/clipart/widgets/properties-nudge-buybox.svg"

export default function PropertiesWidget() {
  const { data: userProfile } = useUser()
  const { badges: badgeCounts } = userProfile || {}

  const { myLeadsEnabled } = useMyLeadsBoostFeature()
  const { shouldNudgeToSignUp, shouldNudgeBuyBoxSetup } =
    useMyLeadsBoostFeature()

  const totalNewMyLeadsCount = badgeCounts?.unreadAnalysisCount || 0
  const totalNewBuyBoxMyLeadsCount = badgeCounts?.unreadBuyBoxCount || 0

  const displayCount = totalNewBuyBoxMyLeadsCount || 0
  const countDescription = "Buy Box Deals"
  const totalCount = totalNewMyLeadsCount || 0

  const showIndicatorAndTotal = totalCount > 0

  // To test....
  // const hasMyLeads: boolean = true

  const percentageAround: number =
    Math.round((displayCount / (totalCount || 0)) * 100) || 0

  // To Test:
  // let percentageAround: number = 100

  const calculateDialAngle = (percentage: number): number => {
    const angle = (percentage / 100) * 262 - 130
    return Math.round(angle)
  }

  const rotation = useSharedValue(-130)
  const rotate: string = rotation.value === 0 ? "0deg" : `${rotation.value}deg`
  const animatedStyle = useAnimatedStyle(() => ({
    position: "absolute",
    width: "100%",
    height: 132,
    justifyContent: "center",
    alignItems: "center",
    marginTop: -3,
    transform: [{ rotate }],
  }))

  useViewedCallback(() => {
    incrementWidgetViewCount()
  }, [])

  useEffect(() => {
    const angle: number = calculateDialAngle(percentageAround)
    rotation.value = withSpring(angle, {
      damping: 10, // Adjust the damping for a less bouncy spring (higher values mean less oscillation)
      stiffness: 20, // Adjust the stiffness of the spring (higher values mean a stiffer spring)
      mass: 1.1, // Adjust the mass (higher values will require more force to move)
    })
  }, [rotation, percentageAround])

  const ctaText = `Never miss the perfect deal`

  const onPress = () => {
    trackClickEvent()
    if (shouldNudgeBuyBoxSetup) {
      router.push("/properties")
      router.push("/buybox")
      return
    }

    if (myLeadsEnabled) {
      router.push("/properties")
      return
    }

    router.push("/properties/myleads/connect")
  }

  const trackClickEvent = () => {
    if (shouldNudgeToSignUp) {
      HomeEvents.homepageWidgetClicked({
        nextScreen: "My Leads Onboarding",
        widgetTitle: "Properties - Nudge SignUp",
        widgetType: "widget-myleads",
        widgetValue: undefined,
        widgetCta: "Sign Up Now",
      })
      return
    }

    if (shouldNudgeBuyBoxSetup) {
      HomeEvents.homepageWidgetClicked({
        nextScreen: "Buy Box",
        widgetTitle: "Properties - Nudge BuyBox",
        widgetType: "widget-myleads",
        widgetValue: undefined,
        widgetCta: "Set Up Buy Box",
      })
      return
    }

    HomeEvents.homepageWidgetClicked({
      nextScreen: "Properties",
      widgetTitle: "Properties",
      widgetType: "widget-myleads",
      widgetState: myLeadsEnabled ? "non-zero-value" : "zero-value",
      widgetValue: myLeadsEnabled ? displayCount.toString() : undefined,
      widgetCta: !myLeadsEnabled ? ctaText : undefined,
    })
  }

  return (
    <WidgetWrapper
      title="Properties"
      category="properties"
      orientation="vertical"
      highlightedStyle={shouldNudgeToSignUp || shouldNudgeBuyBoxSetup}
      onPress={onPress}
    >
      {shouldNudgeToSignUp ? (
        <NudgeToSignUp onPress={onPress} />
      ) : shouldNudgeBuyBoxSetup ? (
        <NudgeToBuyBoxSetup onPress={onPress} />
      ) : !myLeadsEnabled ? (
        <WidgetCommonFTUContainer
          headerText={ctaText}
          BottomIconComponent={BottomIcon}
        />
      ) : (
        <View className="relative flex-1 mt-1">
          <View className="justify-center items-center">
            <DialComponent
              percentage={percentageAround}
              duration={1042}
              key={`myleads-dial-${percentageAround}`}
            />
          </View>

          {showIndicatorAndTotal && (
            <Animated.View style={animatedStyle}>
              <MyLeadsGaugeIndicator />
            </Animated.View>
          )}

          <View className="absolute flex items-center justify-center w-full mt-5">
            <BFText className="justify-center font-figtree-extrabold text-white text-[40px]">
              {displayCount}
            </BFText>
            <BFText className="text-center text-white text-[14px] font-spacegrotesk leading-4 tracking-tighter">
              {countDescription}
            </BFText>
          </View>

          <View className="flex-row items-center justify-center w-full">
            {showIndicatorAndTotal && (
              <BFText className="text-center text-white text-[14px] font-normal font-spacegrotesk leading-none tracking-tight pl-8 mt-[-20px]">
                0
              </BFText>
            )}
            <Spacer />
            {showIndicatorAndTotal && (
              <BFText className="text-center text-white text-[14px] font-normal font-spacegrotesk leading-none tracking-tight pr-8 mt-[-20px]">
                {totalCount}
              </BFText>
            )}
          </View>
        </View>
      )}
    </WidgetWrapper>
  )
}

interface NudgeToSignUpProps {
  onPress: () => void
}

const NudgeToSignUp: React.FC<NudgeToSignUpProps> = ({ onPress }) => {
  return (
    <>
      <View className="w-full justify-start items-start mt-1">
        <H3 className="text-left">{`Meet your\nNEW go-to\ndeal tool!`}</H3>
      </View>
      <PocketImage
        style={{
          position: "absolute",
          bottom: 8,
          right: -4,
        }}
      />
    </>
  )
}

const NudgeToBuyBoxSetup: React.FC<NudgeToSignUpProps> = ({ onPress }) => {
  return (
    <>
      <View className="w-full items-start mt-1">
        <H3 className="text-left">{`Customize\nyour own\nBuy Box`}</H3>
      </View>
      <GiftImage
        style={{
          position: "absolute",
          bottom: 8,
          right: -4,
        }}
      />
    </>
  )
}
